import type { GeneratedRoof } from "building-types/roofs/generatedRoofSystemTypes";
import type { MultiGableRoofClipContext } from "building-types/roofs/multiGableRoofClipContextTypes";
import type { RoofPerimeterRail } from "building-types/roofs/roofPerimeterTypes";
import { RoofPerimeterRailCondition } from "building-types/roofs/roofPerimeterTypes";
import type { RoofSide } from "building-types/settings/roofTypes";
import { RoofBoundaryType } from "building-types/settings/settingsTypesEnum";
import { GABLE_ROOF_THICKNESS } from "common/constants/buildingObjects";
import type { RWTable, Table } from "common/data/table";
import { assertIsNotNull } from "common/utils/assertOfType";
import { MULTIGABLE_BSP_EPSILON } from "doc/utils/roofTools";
import type { Face } from "geom/brep";
import { faceOrNullCopy, faceOrNullMove, faceNew, faceOrNullReverse } from "geom/brep";
import {
  addOutsidePolygonsToBSP,
  addPolygonsToBSP,
  buildPolygonsFromFaces,
  isValidFaceForBSPVolume,
  isValidNodeBSP,
  polygonBSPNew,
} from "geom/bsp";
import type { ClipRailContext } from "geom/bspClipping";
import { clippingNodeNew, clipRail3 } from "geom/bspClipping";
import type { NodeBSP, PolygonBSP } from "geom/bspTypes";
import { DIST_EPSILON } from "geom/core";
import { extrusionToBRep } from "geom/extrude";
import { offsetPolyLine2 } from "geom/offset";
import { planeNewXY, planeProjectPolyLine3, polyLine2OnPlane } from "geom/plane";
import { planeBSPByThreePoints } from "geom/planeBSP";
import { polyLine2Concat, polyLine2Reverse, polyLine2SetCW } from "geom/polyLine2";
import { polyLine3EdgeCount, polyLine3EdgeSlopeInZ, polyLine3Length } from "geom/polyLine3";
import { coplanarWallFilter, nonCoplanarRakeFilter, nonCoplanarWallFilter } from "./roofClippingFilters";
import {
  applyRoofPerimeterRailsExtension,
  buildRoofPerimeterRailsTableRTree,
  clipRoofPerimeterRails,
  coplanarWallExtensionFilter,
  nonCoplanarRakeExtensionFilter,
  rakeExtensionAtCoplanarWallFilter,
  reconnectRoofPerimeterRailsTable,
} from "./roofPerimeterRails";

interface CollectOverhangClippingOptions {
  subRoof: GeneratedRoof;
  subRoofs: Table<GeneratedRoof>;
  isWallClipping?: boolean;
  isEaveClipping?: boolean;
  filter?: (subRoof: GeneratedRoof, side: RoofSide, otherSubRoof: GeneratedRoof, otherSide: RoofSide) => boolean;
}

type CollectOverhangClippingFunc = (options: CollectOverhangClippingOptions) => (NodeBSP | null)[];

interface PrepareRoofsPerimeterRailsResult {
  readonly roofPerimeterRailsTable: Table<RoofPerimeterRail[]>;
  readonly rtree: ReturnType<typeof buildRoofPerimeterRailsTableRTree>;
}

interface PrepareRoofPerimeterRailsOptions {
  readonly subRoofs: Table<GeneratedRoof>;
  readonly clipContext: MultiGableRoofClipContext;
  readonly collectOverhangClipping: CollectOverhangClippingFunc;
  readonly ignoreExtensions?: boolean;
}

export function prepareRoofPerimeterRails({
  subRoofs,
  clipContext,
  collectOverhangClipping,
  ignoreExtensions,
}: PrepareRoofPerimeterRailsOptions): PrepareRoofsPerimeterRailsResult {
  const insideRoofBSPs = Object.values(subRoofs)
    .map((subRoof) => buildSubRoofRailDiscardBSP(subRoof, subRoofs, collectOverhangClipping))
    .filter(isValidNodeBSP);

  const overlapRoofBSPs = Object.values(subRoofs)
    .map((subRoof) => buildSubRoofRailOverlapBSP(subRoof))
    .filter(isValidNodeBSP);

  let roofPerimeterRailsTable: RWTable<RoofPerimeterRail[]> = {};

  for (const subRoofId in subRoofs) {
    const subRoof = subRoofs[subRoofId];

    roofPerimeterRailsTable[subRoofId] = prepareSubRoofPerimeterRails({
      subRoof,
      subRoofs,
      clipContext,
      insideRoofBSPs,
      overlapRoofBSPs,
      collectOverhangClipping,
      ignoreExtensions,
    });
  }

  const rtree = buildRoofPerimeterRailsTableRTree(roofPerimeterRailsTable);
  roofPerimeterRailsTable = reconnectRoofPerimeterRailsTable(roofPerimeterRailsTable, rtree);

  let rakeExtensionApplied = false;
  for (const subRoofId in subRoofs) {
    const {
      roofSettings: { rakeExtensionAtCoplanarWall = 0 },
    } = subRoofs[subRoofId];

    if (rakeExtensionAtCoplanarWall > 0) {
      roofPerimeterRailsTable[subRoofId] = applyRakeExtensionAtCoplanarWall(
        roofPerimeterRailsTable[subRoofId],
        rakeExtensionAtCoplanarWall,
        !!ignoreExtensions,
      );
      rakeExtensionApplied = true;
    }
  }

  let rtreeFinal = rtree;
  if (rakeExtensionApplied) {
    rtreeFinal = buildRoofPerimeterRailsTableRTree(roofPerimeterRailsTable);
    roofPerimeterRailsTable = reconnectRoofPerimeterRailsTable(roofPerimeterRailsTable, rtreeFinal);
  }

  return {
    roofPerimeterRailsTable,
    rtree: rtreeFinal,
  };
}

interface PrepareSubRoofPerimeterRailsOptions {
  readonly subRoof: GeneratedRoof;
  readonly subRoofs: Table<GeneratedRoof>;
  readonly clipContext: MultiGableRoofClipContext;
  readonly insideRoofBSPs: NodeBSP[];
  readonly overlapRoofBSPs: NodeBSP[];
  readonly collectOverhangClipping: CollectOverhangClippingFunc;
  readonly ignoreExtensions?: boolean;
}

function prepareSubRoofPerimeterRails({
  subRoof,
  subRoofs,
  clipContext,
  insideRoofBSPs,
  overlapRoofBSPs,
  collectOverhangClipping,
  ignoreExtensions = false,
}: PrepareSubRoofPerimeterRailsOptions): RoofPerimeterRail[] {
  const { roofVolume, roofPerimeterRails, roofSettings } = subRoof;
  const { upperFloorBSP, boundaryType } = roofVolume;
  const { insideBSP } = clipContext;
  const { eaveExtensionAtCoplanarWall = 0, eaveExtensionAtNonCoplanarRake = 0 } = roofSettings;

  let inputRoofRails = roofPerimeterRails;
  let outRoofRails: RoofPerimeterRail[] = [];

  const swapRails = () => {
    const tempRoofRails = inputRoofRails;
    inputRoofRails = outRoofRails;
    outRoofRails = tempRoofRails;
  };

  const clipRails = (clipOptions: ClipRailContext, newEndCondition: RoofPerimeterRailCondition, offset = 0) => {
    outRoofRails = clipRoofPerimeterRails({
      subRoof,
      roofRails: inputRoofRails,
      clipOptions,
      newEndCondition,
      offset,
    });

    swapRails();
  };

  const extendRails = (
    extension: number,
    applySlopeCorrection?: boolean,
    filter?: (roofRails: RoofPerimeterRail, isEnd: boolean) => boolean,
  ) => {
    if (ignoreExtensions) {
      return;
    }

    outRoofRails = applyRoofPerimeterRailsExtension(inputRoofRails, extension, applySlopeCorrection, filter);
    swapRails();
  };

  const discardContext: ClipRailContext = {
    clippingNodes: overlapRoofBSPs.map((bsp) => clippingNodeNew(bsp)),
    coplanarsToFront: false,
    epsilon: MULTIGABLE_BSP_EPSILON,
  };
  clipRails(discardContext, RoofPerimeterRailCondition.NONE, 4 * MULTIGABLE_BSP_EPSILON);

  const insideContext: ClipRailContext = {
    clippingNodes: [clippingNodeNew(insideBSP)],
    epsilon: MULTIGABLE_BSP_EPSILON,
  };
  clipRails(insideContext, RoofPerimeterRailCondition.NONE);

  const insideRoofsContext: ClipRailContext = {
    clippingNodes: insideRoofBSPs.map((bsp) => clippingNodeNew(bsp)),
    epsilon: MULTIGABLE_BSP_EPSILON,
  };
  clipRails(insideRoofsContext, RoofPerimeterRailCondition.NONE, 4 * MULTIGABLE_BSP_EPSILON);

  const nonCoplanarWallClippingBSPs = collectOverhangClipping({
    subRoof,
    subRoofs,
    isEaveClipping: true,
    filter: nonCoplanarWallFilter,
  });
  const nonCoplanarWallOptions: ClipRailContext = {
    clippingNodes: nonCoplanarWallClippingBSPs.map((bsp) => clippingNodeNew(bsp)),
    epsilon: MULTIGABLE_BSP_EPSILON,
  };
  clipRails(nonCoplanarWallOptions, RoofPerimeterRailCondition.NONE);

  const coplanarWallClippingBSPs = collectOverhangClipping({
    subRoof,
    subRoofs,
    isEaveClipping: true,
    filter: coplanarWallFilter,
  });
  const coplanarWallClippingNodes = coplanarWallClippingBSPs.map((bsp) => clippingNodeNew(bsp));
  if (boundaryType !== RoofBoundaryType.CLIP_TO_PERIMETER && upperFloorBSP) {
    coplanarWallClippingNodes.push(clippingNodeNew(upperFloorBSP));
  }
  const coplanarWallOptions: ClipRailContext = {
    clippingNodes: coplanarWallClippingNodes,
    epsilon: MULTIGABLE_BSP_EPSILON,
  };
  clipRails(coplanarWallOptions, RoofPerimeterRailCondition.COPLANAR_WALL);

  const nonCoplanarRakeClippingBSPs = collectOverhangClipping({
    subRoof,
    subRoofs,
    isEaveClipping: true,
    filter: nonCoplanarRakeFilter,
  });
  const nonCoplanarRakeOptions: ClipRailContext = {
    clippingNodes: nonCoplanarRakeClippingBSPs.map((bsp) => clippingNodeNew(bsp)),
    epsilon: MULTIGABLE_BSP_EPSILON,
  };
  clipRails(nonCoplanarRakeOptions, RoofPerimeterRailCondition.NON_COPLANAR_RAKE);

  extendRails(eaveExtensionAtCoplanarWall, false, coplanarWallExtensionFilter);

  const rakeSlope = 1;
  const rakeFasciaSize = roofSettings.rakeFasciaSize;
  const alpha = Math.abs(Math.atan(rakeSlope));
  const beta = Math.PI / 2 - alpha;
  const extensionAtNonCoplanarRake =
    eaveExtensionAtNonCoplanarRake + (rakeFasciaSize + GABLE_ROOF_THICKNESS) / Math.cos(beta);

  extendRails(extensionAtNonCoplanarRake, false, nonCoplanarRakeExtensionFilter);

  return inputRoofRails;
}

function applyRakeExtensionAtCoplanarWall(
  roofPerimeterRails: RoofPerimeterRail[],
  extension: number,
  ignoreExtensions: boolean,
): RoofPerimeterRail[] {
  let inputRoofRails = roofPerimeterRails;
  let outRoofRails: RoofPerimeterRail[] = [];

  const swapRails = () => {
    const tempRoofRails = inputRoofRails;
    inputRoofRails = outRoofRails;
    outRoofRails = tempRoofRails;
  };

  const extendRails = (
    extensionValue: number,
    applySlopeCorrection?: boolean,
    filter?: (roofRails: RoofPerimeterRail, isEnd: boolean) => boolean,
  ) => {
    if (ignoreExtensions) {
      return;
    }

    outRoofRails = applyRoofPerimeterRailsExtension(inputRoofRails, extensionValue, applySlopeCorrection, filter);
    swapRails();
  };

  extendRails(extension, true, rakeExtensionAtCoplanarWallFilter);
  return inputRoofRails;
}

export function buildSubRoofRailOverlapBSP(roof: GeneratedRoof): NodeBSP | null {
  const { roofTopPlates } = roof;

  const polygons: PolygonBSP[] = [];
  for (const roofTopPlate of roofTopPlates) {
    if (!isValidFaceForBSPVolume(roofTopPlate.profile, GABLE_ROOF_THICKNESS, MULTIGABLE_BSP_EPSILON)) {
      continue;
    }

    const { faces } = extrusionToBRep(roofTopPlate);
    buildPolygonsFromFaces({ faces, polygons });
  }

  return addPolygonsToBSP(null, null, polygons, MULTIGABLE_BSP_EPSILON);
}

export function buildSubRoofRailDiscardBSP(
  roof: GeneratedRoof,
  roofs: Table<GeneratedRoof>,
  collectOverhangClipping: CollectOverhangClippingFunc,
): NodeBSP | null {
  let discardBSP: NodeBSP | null = null;

  const { roofTopFaces, roofSettings, roofPerimeterRails } = roof;
  const { eaveFasciaSize, rakeFasciaSize } = roofSettings;
  const volumeHeight = Math.max(eaveFasciaSize, rakeFasciaSize, GABLE_ROOF_THICKNESS);

  const addDiscardPolygons = (face: Face, height: number) => {
    const { loops, plane } = face;

    if (!isValidFaceForBSPVolume(face, height, MULTIGABLE_BSP_EPSILON)) {
      return;
    }

    const polygons: PolygonBSP[] = [];
    buildPolygonsFromFaces({ faces: [face], polygons });

    const faceMoved = faceOrNullMove(faceOrNullReverse(faceOrNullCopy(face)), 0, 0, -height);
    assertIsNotNull(faceMoved);
    buildPolygonsFromFaces({ faces: [faceMoved], polygons });

    for (const loop of loops) {
      const pl3 = polyLine2OnPlane(plane, loop);
      for (let i = 0, l = pl3.length - 3; i < l; i += 3) {
        const x1 = pl3[i];
        const y1 = pl3[i + 1];
        const z1 = pl3[i + 2];
        const x2 = pl3[i + 3];
        const y2 = pl3[i + 4];
        const z2 = pl3[i + 5];
        const x3 = x2;
        const y3 = y2;
        const z3 = z2 - height;
        const x4 = x1;
        const y4 = y1;
        const z4 = z1 - height;

        const edgePlane = planeBSPByThreePoints(x1, y1, z1, x2, y2, z2, x3, y3, z3);
        if (!edgePlane) {
          continue;
        }

        polygons.push(polygonBSPNew(0, [x1, y1, z1, x2, y2, z2, x3, y3, z3, x4, y4, z4], edgePlane));
      }
    }

    discardBSP = addOutsidePolygonsToBSP({
      node: discardBSP,
      polygons,
      coplanarsToFront: true,
      epsilon: MULTIGABLE_BSP_EPSILON,
    });
  };

  for (const { face } of roofTopFaces) {
    addDiscardPolygons(face, volumeHeight);
  }

  const overhangClippingBSPs = collectOverhangClipping({
    subRoof: roof,
    subRoofs: roofs,
    isEaveClipping: true,
  });

  const overhangClippingOptions: ClipRailContext = {
    clippingNodes: overhangClippingBSPs.map((bsp) => clippingNodeNew(bsp)),
    epsilon: MULTIGABLE_BSP_EPSILON,
  };

  for (const { rail, overhang, fasciaBoardSize } of roofPerimeterRails) {
    if (!rail || overhang < MULTIGABLE_BSP_EPSILON || fasciaBoardSize < MULTIGABLE_BSP_EPSILON) {
      continue;
    }

    const railStartSlope = Math.abs(polyLine3EdgeSlopeInZ(rail, 0));
    const railEndSlope = Math.abs(polyLine3EdgeSlopeInZ(rail, polyLine3EdgeCount(rail) - 1));
    if (railStartSlope > DIST_EPSILON || railEndSlope > DIST_EPSILON) {
      continue;
    }

    const plane = planeNewXY(0, 0, rail[2]);
    const clippedRails = clipRail3({ rail, options: overhangClippingOptions });

    for (const clippedRail of clippedRails) {
      if (polyLine3Length(clippedRail) < MULTIGABLE_BSP_EPSILON) {
        continue;
      }

      const rail2 = planeProjectPolyLine3(plane, clippedRail);
      const innerRail2 = polyLine2Reverse(offsetPolyLine2(rail2, -overhang));
      const perimeter = polyLine2SetCW(polyLine2Concat(rail2, innerRail2, true));
      const face = faceNew([perimeter], plane);
      addDiscardPolygons(face, fasciaBoardSize);
    }
  }

  return discardBSP;
}
